/**
 * Alsunu — global scroll-reveal engine.
 *
 * Every `.als-r` on the page is revealed by this one observer. Sections used
 * to each ship their own copy of this loop inside an inline <script>; that
 * meant a new section either duplicated the logic or — as the shop rail did
 * on first build — silently shipped content stuck at opacity 0 because
 * nobody was watching it.
 *
 * Notes:
 * • Elements already in view on load are revealed on the first callback, so
 *   above-the-fold content is never held back waiting for a scroll.
 * • Each element is unobserved once revealed. The reveal is a one-way door;
 *   re-animating on scroll-back is the kind of motion that reads as noise.
 * • `scan()` is exposed for anything that injects `.als-r` markup after load
 *   (the shop rail's category swap calls it), so dynamic content joins the
 *   same system instead of inventing a second one.
 * • With `prefers-reduced-motion` the CSS already resolves `.als-r` to its
 *   visible end state, so this simply marks everything in-view immediately.
 */
(() => {
  if (window.__alsMotion) return;

  const SELECTOR = '.als-r';
  const seen = new WeakSet();

  const revealAll = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach((el) => el.classList.add('is-in'));
  };

  // No IntersectionObserver (or reduced motion): show everything, immediately.
  if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const run = () => revealAll();
    if (document.readyState !== 'loading') run();
    else document.addEventListener('DOMContentLoaded', run, { once: true });
    window.__alsMotion = { scan: revealAll };
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    },
    // Fires a little before the element's top edge arrives, so the reveal is
    // finishing as it comes into view rather than starting once it is there.
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  const scan = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      observer.observe(el);
    });
  };

  const boot = () => scan();
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot, { once: true });

  // Theme editor re-renders a section without a page load.
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    scan(target instanceof Element ? target : document);
  });

  window.__alsMotion = { scan, revealAll };
})();
